'use client';
import React, { FC, useEffect, useState, useRef } from 'react';
import { mutate } from 'swr';

import {
  TypeImage,
  TypeList,
  TypeMediaTextTitle,
  TypePage,
  TypePlayKitPage,
  TypeProduct,
} from '@/core/generated-types';
import {
  CardsRow,
  HeaderText,
  LuvImage,
  YotpoReviewsWidget,
  TrustedShopReviews,
  ValuePropComponentRow,
  RichText,
  Modal,
  DynamicComponent,
  Button,
} from '@/components';
import { useRouter, usePathname } from '@/navigation';
import { ShopifyProduct } from '@/lib/shopify';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Seo } from '@/components/ui/Seo';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import PlaykitSchema from '@/components/common/Schema/PlaykitSchema';
import {
  ExtendedProductFields,
  buildCanonicalUrl,
} from '@/core/contentfulConfig';
import ProductSchema from '@/components/common/Schema/ProductSchema';
import { Entry } from 'contentful';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
  getLocaleInfo,
} from '@/utils/helpers';
import { ReviewTotals } from '@/lib/yotpo';
import ReviewsSchema from '@/components/common/Schema/ReviewsSchema';
import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
  PRODUCT_ADDED_PAGE_TYPES,
} from '@/utils/rudderstack';
import { useSearchParams } from 'next/navigation';
import { Document } from '@contentful/rich-text-types';
import { StartingKit, useStartingKit } from '@/hooks/useStartingKit';
import { parseDigiohDob } from '@/utils/dates/dateFormatters';
import useLocale from '@/hooks/useLocale';
import { useLocalStorage } from 'usehooks-ts';
import { setLocalStorage } from '@/utils/localStorage';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import buildShopSubdomainUrl from '@/utils/shopSubdomain';
import {
  getDOBByAnchor,
  getTranslations,
  mapTitleToAnchor,
  QR_DATA_KEY,
  findVariantByKey,
  formatCentsToDollars,
} from '@/components/subflow-v3/components/utils';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { PlayKitDetailsHeader } from '@/components/subflow-v3/components/PlayKitDetailsHeader';
import { Divider } from '@/components/subflow-v3/components/Divider';
import Customizations from '@/components/subflow-v3/components/Customizations';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PAYMENT_OPTIONS_URL } from '@/constants/links';
import useFeature from '@/hooks/useFeature';
import { addToCart } from '@/lib/cart.browser';
import { CartItemProperty, ITEM_ORIGINS } from '@/core/constants';
import { TypeTariffSettings } from '@/components/common/types';
import { InventoryStatusCopy } from '@/lib/contentful';
import { ProductContextProvider } from '@/context/ProductContext';

interface ListItemInterface {
  listItems: TypeMediaTextTitle[];
  headerText: string;
}

const ForTheParents = ({
  forTheParentsList,
}: {
  forTheParentsList: TypeList;
}) => {
  return (
    <>
      {/* Workaround to not change parent's shared component style */}

      <div className="for-parents-streamlined bg-white mt-7 -mx-6 md:mx-0 lg:mt-16">
        <HeaderText
          classes="text-center mb-2 md:text-3xl lg:text-3xl"
          size={HeaderSize.H2}
          text={forTheParentsList?.fields?.title || 'For Parents'}
        />
        <div className="-mt-20 -pb-20 md:pb-0">
          <ValuePropComponentRow
            components={
              forTheParentsList.fields.listItems as TypeMediaTextTitle[]
            }
            entryName={
              forTheParentsList.fields.entryName || 'parent list items'
            }
            display="carousel"
          />
        </div>
      </div>
    </>
  );
};

const LookInside = ({ listItems, headerText }: ListItemInterface) => {
  if (!listItems || !listItems.length) return null;
  return (
    <>
      <div
        className="mx-auto max-w-[1200px] px-6 pt-6 md:px-0"
        id="what-is-inside"
      >
        <HeaderText
          classes="mb-5 text-center lg:text-3xl lg:mb-9"
          size={HeaderSize.H2}
          text={headerText}
        />
        <div className="lg:grid lg:grid-cols-3 lg:gap-x-6 lg:gap-y-12">
          {listItems.map((item, i) => (
            <div
              key={`${item.sys.id}-${i}`}
              className="mb-6 flex flex-col text-center w-full items-start gap-1 lg:mb-0"
            >
              <div className="flex-none">
                <LuvImage
                  imageData={item?.fields?.image}
                  width={328}
                  height={328}
                />
              </div>
              <div className="flex-auto">
                <HeaderText
                  size={HeaderSize.H3}
                  text={item?.fields?.textHeading}
                />
                {item?.fields?.textBody && (
                  <div className="mt-1">
                    <RichText content={item.fields.textBody as Document} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export interface PlayKitPageLayoutProps extends TypePlayKitPage {
  shopifyProduct: ShopifyProduct | null;
  yotpoReviews?: ReviewTotals;
  theThinkerPlayKit: StartingKit;
  v3Translations: TypePage;
  tariffSettings?: TypeTariffSettings;
  inventoryStatusCopy?: InventoryStatusCopy | null;
}

const defaultSubflowPath = '/pages/subscription-v3/child-info';

export const PlayKitPageLayout: FC<PlayKitPageLayoutProps> = ({
  shopifyProduct,
  yotpoReviews,
  v3Translations,
  theThinkerPlayKit,
  tariffSettings,
  inventoryStatusCopy,
  ...page
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const t = getTranslations(v3Translations);
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);
  const [language] = useLocalStorage(
    'subflow-v3-language',
    localeInfo.language,
  );
  const { api, data: subflowData } = useSubFlowV3Data();
  const { 'growth_pdp-flexibility': growthPdpFlexibility } = useFlags();
  const increasePerceivedPKSubValue = useFeature(
    'money_increase-perceived-pk-sub-value',
  );
  const isSubscribeAndSave =
    useFeature('money_enable-subscribe-and-save') === 'enabled';

  useEffect(() => {
    if (!shopifyProduct) return;
    const [variant] = shopifyProduct.variants;
    const qtyVal = 1;
    pushEvent('productView', { qtyVal, shopifyProduct, variant });
    captureProductFieldsEvent(
      PRODUCT_EVENT_TYPES.PRODUCT_VIEWED,
      {
        shopifyProduct,
        variant,
        product: page.fields.product.fields,
        quantity: qtyVal,
        url: pathname || '',
      },
      ...(isSubscribeAndSave
        ? [
            undefined,
            undefined,
            formatCentsToDollars(variant.priceCentsStandalone).toString(),
          ]
        : []),
    );
  }, [
    page.fields.product.fields,
    pathname,
    shopifyProduct,
    isSubscribeAndSave,
  ]);

  const {
    fields: {
      product,
      lookInsideList,
      carouselVideo,
      forTheParentsList,
      marketingComponents,
      disclamer,
      relatedProducts,
      enableYotpoReviewsWidget,
      enableTrustedShopsReviewsWidget,
      enableIndividualPlayKitParts,
      tabsComponent,
      aboutHeading,
      productDescription,
      slug,
      alternativeTariffText,
      alternativeTariffTooltip,
      subscribeCtaMessage,
    },
  } = page;

  const updatedTariffSettings = {
    exclusionList: tariffSettings?.exclusionList || [],
    translations: {
      tariffMessage: alternativeTariffText
        ? alternativeTariffText
        : tariffSettings?.translations?.tariffMessage || '',
      tariffTooltip: alternativeTariffTooltip
        ? alternativeTariffTooltip
        : tariffSettings?.translations?.tariffTooltip || '',
    },
  };

  setLocalStorage(LOCAL_STORAGE_KEYS.playKitSeen, {
    title: product.fields.title,
    image: `https:${product.fields.listingImage.fields.Image.fields.file.url}`,
    link: pathname,
  });

  const metadataFields = page.fields?.pageMetafields?.fields;
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] =
    useState(false);

  const hasCustomizations =
    !!theThinkerPlayKit?.customizable_options.length || false;

  const handleAddToCart = async (variant: ShopifyProduct['variants'][0]) => {
    const quantity = 1;
    try {
      const response = await addToCart(
        [
          {
            merchandiseId: variant.id,
            quantity,
            attributes: [
              {
                key: CartItemProperty.PriceSource,
                value: 'standalone',
              },
              {
                key: CartItemProperty.Source,
                value: 'product_page',
              },
              { key: CartItemProperty.QuantityLimitedToOne, value: 'false' },
              {
                key: CartItemProperty.ItemOrigin,
                value: ITEM_ORIGINS.PRODUCT_PAGE,
              },
              {
                key: 'display_price',
                value: variant.priceCentsStandalone?.toString() || '',
              },
            ],
          },
        ],
        quantity,
        { noredirect: 1 },
      );

      if (!response.ok) {
        throw new Error('addToCart failed to execute');
      }

      mutate('/cart', { revalidate: true });

      if (shopifyProduct) {
        pushEvent('addToCart', {
          qtyVal: quantity,
          shopifyProduct,
          variant,
        });
        captureProductFieldsEvent(
          PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
          {
            shopifyProduct,
            variant,
            product: page.fields.product.fields,
            quantity,
            url: pathname || '',
          },
          PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA,
          PRODUCT_ADDED_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
          formatCentsToDollars(variant.priceCentsStandalone),
        );
      }
      const cartUrl = buildShopSubdomainUrl('/cart', locale);
      window.location.href = cartUrl;
    } catch (error) {
      console.error('addToCart failed to execute', error);
    }
  };

  const handleProductNavigation = () => {
    const path = product && product.fields?.cta?.fields.linkUrl;
    router.push(path || defaultSubflowPath);
  };

  const handleCustomizationModal = async () => {
    if (
      subflowData.purchaseOption?.sys?.id?.includes('standalone') &&
      isSubscribeAndSave
    ) {
      const customizationVariant = findVariantByKey(
        shopifyProduct?.variants ?? [],
        subflowData.customizationId || '',
      );
      await handleAddToCart(customizationVariant);
    } else {
      handleProductNavigation();
    }
  };

  const onCTAClick = async (productSelected?: TypeProduct) => {
    if (hasCustomizations) {
      setIsCustomizationModalOpen(true);
    } else if (
      productSelected?.sys?.id?.includes('standalone') &&
      isSubscribeAndSave &&
      shopifyProduct
    ) {
      await handleAddToCart(shopifyProduct?.variants[0]);
    } else {
      handleProductNavigation();
    }
  };

  const searchParams = useSearchParams();
  const qrTargetProduct = searchParams?.get('qrTargetProduct');

  const digiohDobValue =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('digioh_dob')
      : null;
  const formattedDigiohDob = parseDigiohDob(digiohDobValue || '');

  const anchor = mapTitleToAnchor(product.fields.title);

  const dobFromAnchor = growthPdpFlexibility
    ? getDOBByAnchor(anchor as string)
    : null;

  const { data: startingKit, isLoading: isStartingKitLoading } = useStartingKit(
    formattedDigiohDob,
    language,
  );

  useEffect(() => {
    if (isStartingKitLoading || !api) return;
    const formattedDobFromAnchor = parseDigiohDob(dobFromAnchor || '');

    if (growthPdpFlexibility && formattedDobFromAnchor) {
      api.updateChildInfo(
        {
          month: formattedDobFromAnchor.month,
          day: formattedDobFromAnchor.day,
          year: formattedDobFromAnchor.year,
        },
        true,
      );
      // TODO: Update linkUrl in the first loop to correctly redirect to PAYMENT_OPTIONS_URL.
      // Ensure this does not cause an infinite loop or disrupt router functionality.
      if (product.fields.cta) {
        window.localStorage.setItem('subflow-v3-skipped-child-info', 'true');
        product.fields.cta.fields.linkUrl = PAYMENT_OPTIONS_URL;
      }

      if (product.fields.secondaryCta) {
        product.fields.secondaryCta.fields.linkUrl = PAYMENT_OPTIONS_URL;
      }
    } else if (formattedDigiohDob) {
      api.updateChildInfo({
        month: formattedDigiohDob.month,
        day: formattedDigiohDob.day,
        year: formattedDigiohDob.year,
      });

      const ageRange = product.fields.ageRange || product.fields.ageDescription;

      if (
        product.fields.cta &&
        ageRange &&
        startingKit?.age_band === ageRange
      ) {
        window.localStorage.setItem('subflow-v3-skipped-child-info', 'true');
        product.fields.cta.fields.linkUrl = PAYMENT_OPTIONS_URL;
      }
    }
    // TODO(lint): Fix this dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartingKitLoading, startingKit, growthPdpFlexibility]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!window || !qrTargetProduct || qrTargetProduct !== 'kit-the-charmer')
        return window.localStorage.setItem(
          QR_DATA_KEY,
          JSON.stringify({ lookerInTargetQR: false }),
        );
      window.localStorage.setItem(
        QR_DATA_KEY,
        JSON.stringify({ lookerInTargetQR: true }),
      );
    }
  }, [qrTargetProduct]);

  const bottomMarkerRef = useRef<HTMLDivElement | null>(null);

  return (
    <ProductContextProvider
      value={{
        enableYotpoReviewsSummary: shouldUseYotpoReviews(),
        enableTrustedShopsReviewsSummary: shouldUseTrustedShopsReviews(),
        product,
        shopifyProduct,
        inventoryStatusCopy,
      }}
    >
      <div className="playkit-pdp playkit-pdp-streamlined">
        <PlaykitSchema product={product} slug={slug} />
        {shopifyProduct && (
          <div itemScope itemType="https://schema.org/Product">
            <ProductSchema product={product as Entry<ExtendedProductFields>} />
            {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
              <ReviewsSchema
                totals={yotpoReviews}
                product={product as Entry<ExtendedProductFields>}
              />
            )}
          </div>
        )}
        <Seo
          title={metadataFields?.pageTitle}
          description={metadataFields?.pageDescription}
          nofollow={metadataFields?.noFollow}
          noindex={metadataFields?.noIndex}
          canonical={buildCanonicalUrl(page)}
        />
        <div className="mx-auto max-w-[1200px] px-6 pt-6 md:px-10">
          <PlayKitDetailsHeader
            variant={increasePerceivedPKSubValue}
            productDescription={productDescription}
            product={product}
            carousel={[
              ...(carouselVideo ? [carouselVideo] : []),
              ...(lookInsideList?.fields?.listItems?.map(
                ({ fields }) => fields?.image,
              ) as TypeImage[]),
            ]}
            aboutHeading={aboutHeading || ''}
            noCommitmentMessage={t.noCommitmentMessage}
            featuresHeading={t.keyFeatures}
            onCTAClick={onCTAClick}
            tariffSettings={updatedTariffSettings}
            enableIndividualPlayKitParts={enableIndividualPlayKitParts}
            tabsComponent={tabsComponent}
            bottomMarkerRef={bottomMarkerRef}
            subscribeCtaMessage={subscribeCtaMessage}
          />
        </div>
        {lookInsideList?.fields?.listItems && (
          <>
            <Divider className="mt-8 relative lg:mt-20" />
            <div className="bg-aqua-tint py-10 px-6 -mt-5">
              <LookInside
                listItems={
                  lookInsideList?.fields?.listItems as TypeMediaTextTitle[]
                }
                headerText={t.insideThePlayKit}
              />
              <div className="text-center mx-6 lg:mt-16">
                {product.fields.cta && (
                  <Button
                    type="submit"
                    ctaText={product.fields.cta.fields.linkText}
                    classes="mt-5 h-14 w-full lg:h-16 lg:max-w-[360px] md:mt-8"
                    onClick={onCTAClick}
                    fullWidth
                  />
                )}
                <p
                  className="pb-2 mt-3 text-base md:text-lg lg:mt-4"
                  dangerouslySetInnerHTML={{ __html: t.noCommitmentMessage }}
                />
              </div>
            </div>
          </>
        )}
        <div className="mx-auto">
          {marketingComponents &&
            marketingComponents.map((component) => (
              <DynamicComponent key={component.sys.id} component={component} />
            ))}
        </div>
        <div className="mx-auto max-w-[1200px] px-6 pt-6 md:px-10">
          <ForTheParents forTheParentsList={forTheParentsList} />
          {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
            <YotpoReviewsWidget product={product} />
          )}
          {disclamer && <DynamicComponent component={disclamer} />}
          {shouldUseTrustedShopsReviews() &&
            enableTrustedShopsReviewsWidget && (
              <div className="container mx-auto mb-24 px-6">
                <TrustedShopReviews
                  shopifyProduct={shopifyProduct}
                  widgetType="reviews"
                />
              </div>
            )}
        </div>
        {relatedProducts && (
          <>
            <div className="mx-auto my-12">
              <CardsRow {...relatedProducts.fields} />
            </div>
          </>
        )}
        {/* Used anchor ProductAddons summary sticky component above the footer */}
        <div ref={bottomMarkerRef} />
        {hasCustomizations && isCustomizationModalOpen && (
          <Modal
            isOpen={hasCustomizations && isCustomizationModalOpen}
            handleClose={() => setIsCustomizationModalOpen(false)}
            contentClasses="flex justify-center flex-col items-center pb-6 -mb-4 px-1 md:px-6"
          >
            <Customizations t={t} kit={theThinkerPlayKit} />
            {product.fields.secondaryCta && (
              <div className="mx-6 w-full flex justify-center">
                <Button
                  type="submit"
                  ctaText={product.fields.secondaryCta.fields.linkText}
                  onClick={() => {
                    setIsCustomizationModalOpen(false),
                      handleCustomizationModal();
                  }}
                  classes="mt-5 h-14 w-full lg:h-16 lg:max-w-[360px] md:mt-8"
                />
              </div>
            )}
          </Modal>
        )}
      </div>
    </ProductContextProvider>
  );
};
